export type VideoPlayerKind = "iframe" | "react-player" | "pinterest";

export interface ParsedVideoUrl {
  kind: VideoPlayerKind;
  src: string;
  originalUrl: string;
  pinterestPinId?: string;
}

export function extractPinterestPinId(url: string): string | null {
  const match = url.match(/\/pin\/(\d+)/);
  return match?.[1] ?? null;
}

export function isPinterestUrl(url: string): boolean {
  return /(?:^https?:\/\/)?(?:www\.)?(?:pin\.it\/|pinterest\.)/i.test(url);
}

function getPinterestParsedUrl(url: string): ParsedVideoUrl | null {
  if (!isPinterestUrl(url)) return null;

  const pinId = extractPinterestPinId(url);
  return {
    kind: "pinterest",
    src: pinId
      ? `https://assets.pinterest.com/ext/embed.html?id=${pinId}`
      : url,
    originalUrl: url,
    pinterestPinId: pinId ?? undefined,
  };
}

function getDailymotionEmbedUrl(url: string): string | null {
  const match = url.match(/dailymotion\.com\/video\/([a-zA-Z0-9]+)/i);
  return match ? `https://www.dailymotion.com/embed/video/${match[1]}` : null;
}

function getLoomEmbedUrl(url: string): string | null {
  const match = url.match(/loom\.com\/(?:share|embed)\/([a-zA-Z0-9]+)/i);
  return match ? `https://www.loom.com/embed/${match[1]}` : null;
}

function getGoogleDriveEmbedUrl(url: string): string | null {
  const match = url.match(/drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)/i);
  return match ? `https://drive.google.com/file/d/${match[1]}/preview` : null;
}

const IFRAME_RESOLVERS = [
  getDailymotionEmbedUrl,
  getLoomEmbedUrl,
  getGoogleDriveEmbedUrl,
];

export function parseVideoUrl(url: string): ParsedVideoUrl | null {
  if (!url?.trim()) return null;

  const originalUrl = url.trim();

  const pinterest = getPinterestParsedUrl(originalUrl);
  if (pinterest) return pinterest;

  for (const resolver of IFRAME_RESOLVERS) {
    const embedUrl = resolver(originalUrl);
    if (embedUrl) {
      return { kind: "iframe", src: embedUrl, originalUrl };
    }
  }

  return { kind: "react-player", src: originalUrl, originalUrl };
}

export async function resolvePinterestPinId(url: string): Promise<string | null> {
  const directId = extractPinterestPinId(url);
  if (directId) return directId;

  const shortCode = url.match(/pin\.it\/([a-zA-Z0-9]+)/i)?.[1];
  if (!shortCode) return null;

  try {
    const response = await fetch(
      `https://api.pinterest.com/url_shortener/${shortCode}/redirect/`,
      { redirect: "follow" },
    );
    const resolvedId = extractPinterestPinId(response.url);
    if (resolvedId) return resolvedId;
  } catch {
    // CORS or network failure — fall back to Pinterest widget embed.
  }

  return null;
}
